import React, {useEffect, memo} from "react";
import {I18nProvider} from "@lingui/react";
import {Helmet} from "react-helmet";
import {ConnectedRouter} from "connected-react-router";
import {history} from "./redux/store";
import {Route} from "react-router-dom";
import StoreApp from "./containers/Store/StoreApp";
import {ConfigProvider as AntdConfigProvider} from "antd";
import {ThemeProvider} from "styled-components";
import themes from "./settings/themes";
import {themeConfig} from "./settings";
import {GlobalStyles} from "./styles/globalStyle";
import {useDispatch, useSelector} from "react-redux";
import {i18n} from "@lingui/core";
import {antdConfigs, switchLanguage} from "./locales/i18n";
import {t} from "@lingui/macro";
import PublicRouter from "./routers/PublicRouter/router";
import AppActions from './redux/App/actions';
import { LightboxModal } from "./components/LightBox/core/LightboxModal";
import { BukkuReactPortal } from "./containers/App/Portal";

const { setLocaleLoaded } = AppActions;
const {hostname} = window.location;
const {pathname} = window.location;
const {
    REACT_APP_URL_MODE: urlMode,
    REACT_APP_URL_DOMAIN: urlDomain,
    REACT_APP_HTML_TITLE: htmlTitle,
    REACT_APP_STORE_URL_MODE: storeUrlMode, // single or subdomain
    REACT_APP_STORE_URL_DOMAIN: storeUrlDomain, // localhost or bukku.store
} = process.env;

const storeUrlDevPath = 'store';

let subdomain;
if (urlMode === 'subdomain') // Read subdomain from URL
    subdomain = hostname.substr(0, hostname.indexOf(urlDomain) - 1);

export const defaultTitle = (subdomain ? `${subdomain} @ ` : '') + htmlTitle;

/**
 * Check whether this is the store or app.
 * - subdomain mode: hostname is [subdomain].[storeUrlDomain]
 * - single mode: hostname anything, but path start as /store.
 *
 * @return boolean
 **/
function isStoreUrl() {
    if (storeUrlMode === 'subdomain' && hostname.toLowerCase().endsWith(storeUrlDomain))
        return true;
    return (
        storeUrlMode === 'single' &&
        pathname.toLowerCase().startsWith(`/${storeUrlDevPath}`)
    );
}

function ConnectedStoreApp() {
    const localeLoaded = useSelector(state => state.App.localeLoaded);
    if (!localeLoaded)
        return null;

    return (
        <React.Fragment>
            <Helmet>
                <title>{t`Bukku Store`}</title>
            </Helmet>
            <ConnectedRouter history={history}>
                {
                    (storeUrlMode === 'single') ?
                        <Route path={`/${storeUrlDevPath}/:cs`} component={StoreApp} /> :
                        <Route component={StoreApp} />
                }
            </ConnectedRouter>
        </React.Fragment>
    );
}

function ConnectedBukkuApp() {
    const localeLoaded = useSelector(state => state.App.localeLoaded);
    if (!localeLoaded)
        return null;

    return (
        <React.Fragment>
            <Helmet>
                <title>{ defaultTitle }</title>
            </Helmet>
            <PublicRouter history={history} />

            {/* Placed in top level so lightbox can be reused in both public and restricted (auth) pages */}
            <div className="lightbox-root">
                <BukkuReactPortal />
                <LightboxModal />
            </div>
        </React.Fragment>
    );
}

function ConnectedDashApp() {
    const dispatch = useDispatch();
    const locale = useSelector(state => state.App.locale);

    useEffect(() => {
        switchLanguage(locale)
            .finally(() => {
                dispatch(setLocaleLoaded(true));
            });
    }, [locale, dispatch]);

    return (
        <AntdConfigProvider
            locale={antdConfigs[locale]}
            theme={{
                token: {
                    fontFamily: 'Roboto, sans-serif', // to fix antd inputs having different fonts sometimes
                },
                hashed: false, // true will affect readOnly, audit trail; false wil affect statistics
            }}
        >
            <I18nProvider i18n={i18n} forceRenderOnLocaleChange={false}>
                <ThemeProvider theme={themes[themeConfig.theme]}>
                    {isStoreUrl() ? <ConnectedStoreApp /> : <ConnectedBukkuApp />}
                    <GlobalStyles />
                </ThemeProvider>
            </I18nProvider>
        </AntdConfigProvider>
    );
}

// <IntlProvider
//     locale={currentAppLocale.locale}
//     messages={currentAppLocale.messages}
// ></IntlProvider>

export default memo(ConnectedDashApp);
